import { useState, type FormEvent } from "react";
import { contactMeta } from "@/mocks/manga";
import SectionTitle from "./SectionTitle";

const FORM_URL = "https://readdy.ai/api/form/da2a47tv7gurs51efa50";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐检测：若被机器人填写，则伪装成功，不提交
    if (String(data.get("website_alt") || "").trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const okCode = res.ok && parsed?.code === "OK";
      const spam = /spam/i.test(serverMsg);

      if (okCode && !spam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg && serverMsg !== responseText ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  }

  return (
    <section id="contact" className="bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <SectionTitle
              align="left"
              eyebrow="联系我们"
              title="有问题？随时找到我们"
              desc="无论是功能建议、追更问题还是反馈意见，都欢迎与我们联系。你的反馈是汗汗虫虫漫画不断变好的动力。"
            />
            <div className="mt-8 space-y-4">
              {contactMeta.map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${c.icon} text-xl`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
            data-readdy-form="contact"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm font-medium text-foreground-900">
                  你的称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼你"
                  className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground-900">
                  邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm font-medium text-foreground-900">
                留言内容
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                maxLength={500}
                placeholder="请告诉我们你的想法、建议或遇到的问题……"
                className="resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
              ></textarea>
              <p className="text-right text-xs text-foreground-400">最多 500 字</p>
            </div>

            {/* 蜜罐字段，用于拦截机器人 */}
            <input name="website_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

            {status === "success" && (
              <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-700">
                <i className="ri-checkbox-circle-fill"></i>
                提交成功！我们已收到你的留言，会尽快与你联系。
              </p>
            )}
            {status === "error" && formError && (
              <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-700">
                <i className="ri-error-warning-line mt-0.5"></i>
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-6 w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "loading" ? "提交中…" : "提交留言"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}